import { useEffect, useState } from 'react';
import type { RoundSnapshot as ChaosSnapshot } from '@hypevault/chaos-scramble';
import type { RoundSnapshot as EmojiSnapshot } from '@hypevault/emoji-decoder';
import type { GameSnapshot as HypeMillionSnapshot } from '@hypevault/hype-million';
import type { GameSnapshot as HypeDealSnapshot } from '@hypevault/hype-deal';
import type { RoundSnapshot as DrawGuessSnapshot } from '@hypevault/draw-guess';
import { GameWidget } from './GameWidget';
import { ChaosScrambleWidget } from './ChaosScrambleWidget';
import { EmojiDecoderWidget } from './EmojiDecoderWidget';
import { HypeMillionWidget } from './HypeMillionWidget';
import { HypeDealWidget } from './HypeDealWidget';
import { DrawGuessWidget } from './DrawGuessWidget';

/**
 * Router widget for the unified `game` browser source. Reads the per-
 * streamer "active-game" state and renders whichever child widget is in
 * play. Single browser source URL covers all 11 games (6 standard + 5 AI).
 *
 * Bootstrap: GET /api/active-game?token=... on mount. After that the
 * `active-game-changed` socket event drives live updates.
 *
 * Idle state (no active game): renders nothing — overlay is transparent
 * until the streamer starts something.
 */

export type ActiveGameType =
  | 'trivia'
  | 'hangman'
  | 'chatwords'
  | 'feud'
  | 'crossword'
  | 'battleship'
  | 'chaos_scramble'
  | 'emoji_decoder'
  | 'hype_million'
  | 'hype_deal'
  | 'draw_guess';

const STANDARD: ReadonlySet<ActiveGameType> = new Set<ActiveGameType>([
  'trivia',
  'hangman',
  'chatwords',
  'feud',
  'crossword',
  'battleship',
]);

interface Props {
  streamerId: string;
  token: string;
  streamerTier: 'free' | 'paid';
  fields: Record<string, unknown>;
  /** From `active-game-changed` socket event. */
  liveActiveGame: ActiveGameType | null | undefined;
  // Child snapshots, threaded through from WidgetApp/OverlayApp.
  gameStateEvent: {
    gameType: string;
    state: unknown;
    config: unknown;
    isActive: boolean;
  } | null;
  chaosSnapshot: ChaosSnapshot | null;
  emojiSnapshot: EmojiSnapshot | null;
  hypeMillionSnapshot: HypeMillionSnapshot | null;
  hypeDealSnapshot: HypeDealSnapshot | null;
  drawGuessSnapshot: DrawGuessSnapshot | null;
}

export function UnifiedGameWidget({
  streamerId,
  token,
  streamerTier,
  fields,
  liveActiveGame,
  gameStateEvent,
  chaosSnapshot,
  emojiSnapshot,
  hypeMillionSnapshot,
  hypeDealSnapshot,
  drawGuessSnapshot,
}: Props) {
  void fields; // reserved for per-widget overrides

  // Bootstrap from /api/active-game on mount so the widget paints the
  // current game even if the streamer started before the overlay opened.
  const [bootstrapActive, setBootstrapActive] = useState<ActiveGameType | null>(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`/api/active-game?token=${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json) return;
        if (json.activeGame && typeof json.activeGame === 'string') {
          setBootstrapActive(json.activeGame as ActiveGameType);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  // liveActiveGame from socket wins once we've received one; otherwise
  // fall back to the bootstrap.  `undefined` means "no live update yet".
  const activeGame =
    liveActiveGame === undefined ? bootstrapActive : (liveActiveGame as ActiveGameType | null);

  if (!activeGame) return null;

  if (STANDARD.has(activeGame)) {
    return <GameWidget token={token} gameStateEvent={gameStateEvent} streamerTier={streamerTier} />;
  }

  switch (activeGame) {
    case 'chaos_scramble':
      return (
        <ChaosScrambleWidget streamerId={streamerId} snapshot={chaosSnapshot} fields={fields} />
      );
    case 'emoji_decoder':
      return (
        <EmojiDecoderWidget streamerId={streamerId} snapshot={emojiSnapshot} fields={fields} />
      );
    case 'hype_million':
      return (
        <HypeMillionWidget streamerId={streamerId} snapshot={hypeMillionSnapshot} fields={fields} />
      );
    case 'hype_deal':
      return <HypeDealWidget streamerId={streamerId} snapshot={hypeDealSnapshot} fields={fields} />;
    case 'draw_guess':
      return (
        <DrawGuessWidget streamerId={streamerId} snapshot={drawGuessSnapshot} fields={fields} />
      );
    default:
      return null;
  }
}
